import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AssetDto, GalleryDto } from '@picflow/types';
import { calcResizedUrl } from '../lib/calc-resized-url';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { getPageType } from '../lib/get-page-type';
import { AppConfigService } from '../providers/config.service';
import { getFormatsFromFormatParam } from '../tools/convert/get-formats-from-format-param';
import { ConversionFormat } from '../tools/convert/templates/conversion-template/conversion-template.component';
import { BrowserService } from './browser.service';
import { ConvertReplaceService } from './convert-replace.service';
import { StateService } from './state.service';
import { StoryblokService } from './storyblok.service';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  lastUrl: string;
  favIcons: { el: HTMLLinkElement; size: number }[];
  manifest: HTMLLinkElement;

  constructor(
    private meta: Meta,
    private router: Router,
    private route: ActivatedRoute,
    private state: StateService,
    private title: Title,
    private browserService: BrowserService,
    private config: AppConfigService,
    private storyblokService: StoryblokService,
    private convertReplaceService: ConvertReplaceService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.handleMeta(event.urlAfterRedirects);
    });
    this.favIcons = [
      { el: this.document.querySelector('#favicon'), size: 32 },
      { el: this.document.querySelector('#favicon-apple-touch'), size: 180 },
      { el: this.document.querySelector('#favicon-32'), size: 32 },
      { el: this.document.querySelector('#favicon-16'), size: 16 },
    ];
    this.manifest = this.document.querySelector('#manifest');
  }

  public getTitle(url: string, tags?: MetaDefinition[]) {
    if (!tags) tags = this.getMetaTags(url);
    return tags.find(tag => tag.property === 'og:title').content;
  }

  private getFavicon(size: number) {
    const route = getFullPath(this.route.snapshot);
    let favicon = '/assets/picflow-icon.svg';
    const isShared = route[1] === 'galleries' && route[2] === 'shared';

    if (!isShared) {
      if (this.state.unseenActivity) favicon = '/assets/favicon/favicon-alert.ico';

      if (this.config.isTenantDomain) {
        const data = getAllRouteData(this.route.snapshot);
        const tenant = data.tenant || data.gallery?.tenant;

        if (tenant?.favicon_url) {
          favicon = tenant.favicon_url;
          const url = tenant.favicon_url;
          if (!url.includes('/original/')) return favicon; // new icons
          const uuid = url.split('/original/')[1].split('.')[0];
          favicon = calcResizedUrl(this.config.assetsEndpoint, uuid, `${size}x${size}`);
        }
      }
    }

    return favicon;
  }

  private getManifest() {
    if (!this.config.isTenantDomain) return '/manifest.webmanifest';
    const data = getAllRouteData(this.route.snapshot);
    const tenant = data.tenant || data.gallery?.tenant;
    if (!tenant?.favicon_url) return '/manifest.webmanifest';
    // https://assets.dev.picflow.io/tenants/tnt_5C79tWpvmmoyaeb9/42bd4b86-21d6-49f4-9654-2bfabb280932.png"
    // const uid = tenant.favicon_url.split('/original/')[1].split('.')[0];
    const tenantName: string = tenant.name || '';
    return `/manifest/${encodeURIComponent(tenantName.split('.').join('').split(',').join('') || 'Picflow')}`;
  }

  public async handleMeta(url: string = this.lastUrl) {
    if (!url && !this.lastUrl) return;
    this.lastUrl = url;
    const tags = await this.getMetaTags(url);
    let title = this.getTitle(url, tags);

    this.favIcons.forEach(icon => {
      if (icon.el) icon.el.href = this.getFavicon(icon.size);
    });

    const manifestHref = this.getManifest();
    if (this.manifest && !this.manifest.href.endsWith(manifestHref)) {
      this.manifest.href = manifestHref;
    }

    const path = getFullPath(this.route.snapshot);

    if (path[0] === 'a') {
      if (path[1] === 'gallery') {
        const galleryId = path[2];
        const count = this.state.unseenActivityCountMap.get(galleryId);
        if (count) {
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      } else if (this.state.unseenActivity > 0) {
        const isShared = path[1] === 'galleries' && path[2] === 'shared';

        if (!isShared) {
          const count = this.state.unseenActivity;
          const countString = count > 99 ? '99+' : count.toString();
          title = `(${countString}) ${title}`;
        }
      }
    }

    this.title.setTitle(title);

    const link: HTMLLinkElement = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    this.document.head.appendChild(link);
    link.setAttribute('href', this.getCanonical());

    tags.forEach(tag => this.meta.updateTag(tag));
  }

  private getCanonical() {
    const path = getFullPath(this.route.snapshot);
    const origin = this.browserService.getOrigin();
    return `${origin}/${path.join('/')}`;
  }

  private getMetaTags(url: string) {
    const data = getAllRouteData(this.route.snapshot);
    const path = getFullPath(this.route.snapshot);
    let title = getPageType(url, {
      notFound: this.state.nextPageIsNotFound,
      isTenantDomain: this.config.isTenantDomain,
    });
    const tags: MetaDefinition[] = [];
    let imageUrl = 'https://picflow.com/assets/sharing/default.jpg';
    let type = 'website';
    let description = 'The best way for Pro Photographers to Share, Proof and Deliver Work. Try Picflow for Free!';
    let robots = 'index,follow,max-image-preview:large';

    tags.push({ property: 'og:url', content: this.getCanonical() });

    // Gallery
    const gallery = <GalleryDto>data.gallery;
    if (gallery) {
      const tenant = gallery['tenant'].name;
      if (path[0] === 'a') {
        // Edit gallery
        title = `${gallery.name || gallery.title || 'Edit Gallery'} • Picflow`;
      } else {
        // Public gallery
        if (tenant && gallery.title) title = `${gallery.title} by ${tenant}`;
        else if (tenant && !gallery.title) title = `Gallery by ${tenant}`;
        else if (!tenant && gallery.title) title = `${gallery.title}`;
        else title = 'Picflow Gallery';

        if (tenant) description = `Photo Gallery by ${tenant}`;

        if (gallery.teaser.uuid) {
          imageUrl = calcResizedUrl(this.config.assetsEndpoint, gallery.teaser.uuid, `${1200}x${630}q${85}`);
        }
      }
    }
    if (path.length === 0 && data.tenant) {
      if (data.tenant.name) {
        title = data.tenant.name;
        description = `Galleries by ${data.tenant.name}`;
      } else {
        title = 'Galleries';
        description = 'Galleries';
      }
      imageUrl = undefined;
    }

    if ((path[0] === 'gallery' && path[1] === 'access') || path[0] === 'access') {
      title = 'Protected Gallery';
      description = 'This is a protected gallery which requires a password to view';
      imageUrl = 'https://picflow.com/assets/sharing/protected.png';
    }

    // Gallery asset
    if (data.data?.asset && gallery?.['tenant'].path !== 'demo') {
      const asset = <AssetDto>data.data.asset;
      imageUrl = calcResizedUrl(this.config.assetsEndpoint, asset.version_data.uuid, `${1200}x${630}q${85}`);
      title += ' · ' + asset.version_data.asset_name;
    }

    // Storyblok pages
    if (data.story) {
      title = this.storyblokService.getStoryTitle(data.story);
      description = this.storyblokService.getStoryDescription(data.story);
      if (data.story.content.keyVisual?.filename) {
        imageUrl = data.story.content.keyVisual.filename;
        if (imageUrl.endsWith('.svg')) {
          imageUrl = `${imageUrl}/m/1200x0/filters:format(jpg)`;
        }
      }
      if (data.story.content.noIndex) robots = 'noindex,follow';
      if (path[0] === 'blog') {
        type = 'article';
      }
    }
    if (data.story === false) {
      robots = 'noindex,follow';
    }

    // Auth pages etc.
    if (data.meta) {
      const meta = data.meta;
      if (meta.title) title = meta.title;
      if (meta.description) description = meta.description;
      if (meta.imageUrl) imageUrl = meta.imageUrl;
    }

    if (path[0] === 'convert' && path.length === 2) {
      const { from: fromFormatSlug, to: toFormatSlug } = getFormatsFromFormatParam(path[1]);
      const formats = data.formats as ConversionFormat[];

      const fromFormat = fromFormatSlug && formats.find(f => f.slug === fromFormatSlug);
      const toFormat = toFormatSlug && formats.find(f => f.slug === toFormatSlug);

      if (title) {
        title = this.convertReplaceService.replace(title, fromFormat, toFormat);
      }
      if (description) {
        description = this.convertReplaceService.replace(description, fromFormat, toFormat);
      }
      if (imageUrl) {
        imageUrl = this.convertReplaceService.replace(imageUrl, fromFormat, toFormat);
      }
    }

    // Some pages should not be indexed
    if (data.noIndex) robots = 'noindex,follow';

    tags.push({ property: 'og:image', content: imageUrl });
    tags.push({ property: 'og:title', content: title });
    tags.push({ name: 'description', content: description });
    tags.push({ property: 'og:description', content: description });
    tags.push({ property: 'og:type', content: type });
    tags.push({ name: 'robots', content: robots });

    const stopZoom = this.browserService.isTenantDomain() || path[0] === 'a';

    tags.push({
      name: 'viewport',
      content: stopZoom
        ? 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no, shrink-to-fit=no'
        : 'width=device-width, initial-scale=1, viewport-fit=cover',
    });

    // Twitter spesific image
    tags.push({ content: imageUrl, name: 'twitter:image' });

    return tags;
  }
}
